import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsDelegation, useDelegations, useDestinataires } from '@/api/hooks';
import type { Delegation } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';

const PORTEES: Array<[keyof Delegation['portee'], string]> = [
  ['courriers', 'Courriers'],
  ['factures', 'Factures'],
  ['reponses', 'Réponses'],
  ['reaffectation', 'Réaffectations'],
];

const etatDe = (d: Delegation): { libelle: string; variant: 'default' | 'secondary' | 'outline' } => {
  const maintenant = Date.now();
  if (!d.actif) return { libelle: 'Terminée', variant: 'secondary' };
  if (new Date(d.debut).getTime() > maintenant) return { libelle: 'À venir', variant: 'outline' };
  if (new Date(d.fin).getTime() < maintenant) return { libelle: 'Expirée', variant: 'secondary' };
  return { libelle: 'En cours', variant: 'default' };
};

const auj = () => new Date().toISOString().slice(0, 10);

/** Délégations du DG (absence, mission) : délégataire, période, portée ; les confidentiels ne sont jamais délégués. */
export function Delegations() {
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useDelegations();
  const destinataires = useDestinataires();
  const actions = useActionsDelegation();
  const dg = u.profils.includes('DIRECTEUR_GENERAL');
  const [form, setForm] = useState({ delegataireId: '', debut: auj(), fin: auj(), portee: { courriers: true, factures: true, reponses: true, reaffectation: true }, exclusions: '' });

  const creer = async () => {
    try {
      await actions.creer.mutateAsync({ delegataireId: form.delegataireId, debut: `${form.debut}T00:00:00.000Z`, fin: `${form.fin}T23:59:59.000Z`, portee: form.portee, exclusions: form.exclusions.trim() || undefined });
      toast.success('Délégation enregistrée.');
      setForm({ ...form, delegataireId: '', exclusions: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const terminer = async (id: string) => {
    try {
      await actions.terminer.mutateAsync(id);
      toast.success('Délégation terminée.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const candidats = [...(destinataires.data ?? [])].filter((d) => d.agentId !== u.agentId).sort((a, b) => Number(b.profils.some((p) => p.startsWith('DGA'))) - Number(a.profils.some((p) => p.startsWith('DGA'))) || a.nom.localeCompare(b.nom));

  return (
    <div className="space-y-6">
      <PageHeader titre="Délégations" sousTitre="En cas d'absence du DG, le délégataire (en principe un DGA) traite le parapheur ; chaque action est tracée en son nom, pour le compte du titulaire." />
      {dg && (
        <Card>
          <CardHeader>
            <CardTitle>Nouvelle délégation</CardTitle>
            <CardDescription>Les courriers confidentiels restent réservés au DG titulaire, sauf intérim formel.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="delegataire">Délégataire</Label>
              <SelectNatif id="delegataire" value={form.delegataireId} onChange={(e) => setForm({ ...form, delegataireId: e.target.value })}>
                <option value="">Choisir</option>
                {candidats.map((d) => (
                  <option key={d.agentId} value={d.agentId}>
                    {d.nom} · {d.fonction}
                  </option>
                ))}
              </SelectNatif>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="debut">Du</Label>
              <Input id="debut" type="date" value={form.debut} onChange={(e) => setForm({ ...form, debut: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fin">Au (inclus)</Label>
              <Input id="fin" type="date" value={form.fin} onChange={(e) => setForm({ ...form, fin: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Portée</Label>
              <div className="flex flex-wrap gap-4 pt-1 text-sm">
                {PORTEES.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2">
                    <Checkbox checked={form.portee[k as keyof typeof form.portee]} onCheckedChange={(c) => setForm({ ...form, portee: { ...form.portee, [k]: c === true } })} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exclusions">Exclusions (texte libre)</Label>
              <Textarea id="exclusions" rows={2} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} placeholder="Factures supérieures à 15 000 USD exclues" />
            </div>
            <div className="flex justify-end md:col-span-2">
              <Button onClick={creer} disabled={!form.delegataireId || !form.debut || !form.fin || form.fin < form.debut || actions.creer.isPending}>
                Enregistrer la délégation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucune délégation." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titulaire</TableHead>
                <TableHead>Délégataire</TableHead>
                <TableHead>Période</TableHead>
                <TableHead className="hidden md:table-cell">Portée</TableHead>
                <TableHead>État</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => {
                const etat = etatDe(d);
                return (
                  <TableRow key={d.id}>
                    <TableCell>{d.titulaire ? `${d.titulaire.prenom} ${d.titulaire.nom}` : '—'}</TableCell>
                    <TableCell>{d.delegataire ? `${d.delegataire.prenom} ${d.delegataire.nom}` : '—'}</TableCell>
                    <TableCell>
                      {dateFr(d.debut)} → {dateFr(d.fin)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {PORTEES.filter(([k]) => d.portee[k]).map(([k, l]) => (
                          <Badge key={k} variant="secondary">
                            {l}
                          </Badge>
                        ))}
                      </div>
                      {d.exclusions && <div className="text-xs text-muted-foreground">{d.exclusions}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={etat.variant}>{etat.libelle}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {d.actif && (d.titulaireId === u.agentId || u.profils.includes('ADMINISTRATEUR')) && etat.libelle !== 'Expirée' && (
                        <Button size="sm" variant="outline" onClick={() => terminer(d.id)} disabled={actions.terminer.isPending}>
                          Terminer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
